import React, { useState, useEffect, useCallback, useRef } from 'react';
import { produce } from 'immer';
import './App.css';
import './test-animation.css';
import './styles/custom.css';
import Game from './components/Game.js';
import TitleScreen from './components/TitleScreen.js';
import DeckSelectionScreen from './components/DeckSelectionScreen.js';
import CardLibraryScreen from './components/CardLibraryScreen.js';
import { initializeGame, playCard, endTurn, resolveInput, checkGameOver, _proceedToNextTurn, performMulligan, resolveMulliganPhase } from './gameLogic/main.js';
import { HUMAN_PLAYER_ID, NPC_PLAYER_ID, GamePhase } from './gameLogic/constants.js';
import { processEffects } from './gameLogic/effectHandler.js';
import useEnhancedLog from './hooks/useEnhancedLog.js';
import GameOverScreen from './components/GameOverScreen.js';
import DeckBuilderScreen from './components/DeckBuilderScreen.js';
import TutorialSelectionScreen from './components/TutorialSelectionScreen.js';
import { effectMonitor } from './gameLogic/EffectMonitor.js';
import packageJson from '../package.json';
import RulesOverlay from './components/overlays/RulesOverlay.js';
import CreditsOverlay from './components/overlays/CreditsOverlay.js';
import ChangelogModal from './components/overlays/ChangelogModal.js';
import { setLanguage, getLanguage, getUILoc } from './utils/cardUtils.js';
import ja_base from './locales/ja/base.json';
import useWindowSize from './hooks/useWindowSize.js';
import { getFromStorage } from './utils/localStorage.js';
import { recordMatchFromGameState } from './utils/playRecordUtils.js';

// 世の常モード関連のインポート
import { useYonotsune } from './hooks/useYonotsune.js';
import YonotsuneScreen from './components/YonotsuneScreen.js';
import YonotsuneRewardScreen from './components/YonotsuneRewardScreen.js';

// 仮説検証モード関連のインポート
import HypothesisVerificationScreen from './components/hypothesis/HypothesisVerificationScreen.js';

const DEFAULT_HYPOTHESIS_CONFIG = {
  playerDeck: [],
  playerConsciousness: 15,
  playerScale: 5,
  playerInitialHandSize: 4,
  playerAntiDeckOut: false,
  npcDeck: [],
  npcConsciousness: 15,
  npcScale: 5,
  npcInitialHandSize: 4,
  npcAntiDeckOut: false,
  turnOrder: 'player', // 'player' | 'npc' | 'random'
};

function App() {
  const [gameState, setGameState] = useState(null);
  const [gameData, setGameData] = useState({ cardDefs: null, presetDecks: null, tutorialDecks: null, tutorialMaster: null, tutorialScenarios: null });
  const [hypothesisConfig, setHypothesisConfig] = useState(DEFAULT_HYPOTHESIS_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingGameResultAnimation, setPlayingGameResultAnimation] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showTranslationWarning, setShowTranslationWarning] = useState(false);

  // 世の常モードのフック初期化
  const yonotsune = useYonotsune(gameData.cardDefs);

  // ウィンドウサイズとスケーリングの計算
  const { width, height } = useWindowSize();
  const TARGET_HEIGHT = 820; // iPad横画面などの基準高さ
  const MIN_WIDTH_FOR_SCALE = 600; // モバイル縦画面ではスケールさせない
  
  const scale = (height < TARGET_HEIGHT && width >= MIN_WIDTH_FOR_SCALE)
    ? Math.max(0.5, height / TARGET_HEIGHT) // 極端な縮小を防ぐため下限を設定
    : 1;

  const scaleStyle = {
    transform: `scale(${scale})`,
    width: `${100 / scale}%`,
    height: `${100 / scale}%`,
  };
  
  // 言語設定の状態管理
  const [language, setLanguageState] = useState(() => {
    const savedLang = localStorage.getItem('historio_language');
    if (savedLang) {
      setLanguage(savedLang);
      return savedLang;
    }
    return getLanguage();
  });

  // 初期ロード時に言語が日本語以外の場合、警告を表示
  useEffect(() => {
    if (language !== 'ja') {
      setShowTranslationWarning(true);
    }
  }, [language]);

  const handleLanguageChange = useCallback((newLang) => {
    setLanguage(newLang);
    setLanguageState(newLang);
    localStorage.setItem('historio_language', newLang);

    // 日本語以外に切り替えた場合のみ警告を表示
    if (newLang !== 'ja') {
      setShowTranslationWarning(true);
    }
  }, []);
  
  const appVersion = packageJson.version;

  const gameStateRef = useRef(gameState);
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  const [currentScreen, setCurrentScreen] = useState('title');
  const [deckToEdit, setDeckToEdit] = useState(null);

  const isTransitioningRef = useRef(false);
  const gameOverAnimationInitiatedRef = useRef(false);

  const enhancedLog = useEnhancedLog(gameState, effectMonitor);

  useEffect(() => {
    if (gameState && gameState.effect_queue.length > 0 && !gameState.awaiting_input) {
      const newGameState = processEffects(gameState, checkGameOver);
      setGameState(newGameState);
    }
  }, [gameState]);

  useEffect(() => {
    if (gameState && !gameState.awaiting_input && gameState.effect_queue.length === 0 && gameState.processing_status.pending_turn_transition && 
      !isTransitioningRef.current) {
        isTransitioningRef.current = true;
        const newGameState = _proceedToNextTurn(gameState);
        setGameState(newGameState);
        isTransitioningRef.current = false;
    }
  }, [gameState]);

  useEffect(() => {
    if (gameState?.game_over && !playingGameResultAnimation && !gameOverAnimationInitiatedRef.current) {
      gameOverAnimationInitiatedRef.current = true;
      setPlayingGameResultAnimation(true);
    }
  }, [gameState?.game_over, playingGameResultAnimation]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    Promise.all([
      fetch('/card_definitions.json').then(res => {
        if (!res.ok) throw new Error(`Failed to load card definitions: ${res.status}`);
        return res.json();
      }),
      fetch('/preset_decks.json').then(res => {
        if (!res.ok) throw new Error(`Failed to load preset decks: ${res.status}`);
        return res.json();
      }),
      fetch('/tutorials/tutorial_decks.json').then(res => {
        if (!res.ok) throw new Error(`Failed to load tutorial decks: ${res.status}`);
        return res.json();
      }),
      fetch('/tutorials/tutorial_master.json').then(res => {
        if (!res.ok) throw new Error(`Failed to load tutorial master: ${res.status}`);
        return res.json();
      }),
      fetch('/tutorials/tutorial_scenarios.json').then(res => {
        if (!res.ok) throw new Error(`Failed to load tutorial scenarios: ${res.status}`);
        return res.json();
      }),
    ])
    .then(([cardDefs, presetDecks, tutorialDecks, tutorialMaster, tutorialScenarios]) => {
      const defsMap = cardDefs.reduce((acc, def, index) => { 
        // 日本語名をインジェクト（旧セーブデータとの互換性確保のため）
        const loc = ja_base[def.id.toString()] || { name: `ID_${def.id}` };
        acc[def.id] = { ...def, name: loc.name, definitionOrder: index }; 
        return acc; 
      }, {});
      
      setGameData({ 
        cardDefs: defsMap, 
        presetDecks: presetDecks,
        tutorialDecks: tutorialDecks,
        tutorialMaster: tutorialMaster,
        tutorialScenarios: tutorialScenarios,
      });
      setLoading(false);
    })
    .catch(error => {
      console.error("[App] Error loading game data:", error);
      setError(error.message);
      setLoading(false);
    });
  }, []);

  const startGame = useCallback((playerDeckSource, npcDeckSource, tutorialScenario = null, roguelikeParams = null) => {
    if (!gameData.cardDefs || !gameData.presetDecks) {
      setError(getUILoc('MESSAGES.ERROR_LOADING_DATA'));
      return;
    }

    // デッキオブジェクトを解決する（文字列の場合はプリセット→カスタムの順で検索）
    const resolvedeck = (source) => {
      if (typeof source !== 'string') return source;
      const fromPreset = gameData.presetDecks.find(deck => deck.name === source);
      if (fromPreset) return fromPreset;
      const customDecks = getFromStorage('customDecks') || {};
      return customDecks[source] || null;
    };

    const playerDeckObject = resolvedeck(playerDeckSource);
    const npcDeckObject = resolvedeck(npcDeckSource);

    if (!playerDeckObject || !npcDeckObject) {
      setError(getUILoc('MESSAGES.DECK_NOT_FOUND'));
      return;
    }

    try {
      const newGameState = initializeGame(gameData.cardDefs, gameData.presetDecks, playerDeckObject, npcDeckObject, tutorialScenario, roguelikeParams);
      if (!newGameState) {
        throw new Error(getUILoc('MESSAGES.INIT_FAILURE'));
      }
      
      gameOverAnimationInitiatedRef.current = false;
      setGameState(newGameState);
      setCurrentScreen('game');
      setError(null);
    } catch (error) {
      console.error('[App] Error initializing game:', error);
      setError(`${getUILoc('MESSAGES.INIT_FAILURE')}: ${error.message}`);
      setCurrentScreen('title');
    }
  }, [gameData]);

  const handleStartTutorial = useCallback((tutorialId) => {
    console.log('[App] handleStartTutorial called with tutorialId:', tutorialId);
    if (!gameData.cardDefs || !gameData.tutorialDecks || !gameData.tutorialMaster || !gameData.tutorialScenarios) {
      setError(getUILoc('MESSAGES.TUTORIAL_DATA_NOT_LOADED'));
      return;
    }

    const tutorialMasterEntry = gameData.tutorialMaster.find(t => t.tutorialId === tutorialId);
    if (!tutorialMasterEntry) {
      setError(getUILoc('MESSAGES.TUTORIAL_NOT_FOUND', { id: tutorialId }));
      return;
    }

    const playerDeckId = tutorialMasterEntry.playerDeckId;
    const npcDeckId = tutorialMasterEntry.npcDeckId;
    const tutorialScenarioId = tutorialMasterEntry.scenarioId;

    const playerDeck = gameData.tutorialDecks[playerDeckId];
    const npcDeck = gameData.tutorialDecks[npcDeckId];

    if (!playerDeck || !npcDeck) {
        setError(getUILoc('MESSAGES.TUTORIAL_DECK_NOT_FOUND'));
        return;
    }

    const scenarios = gameData.tutorialScenarios.default || gameData.tutorialScenarios;
    const tutorialScenario = scenarios[tutorialScenarioId];

    if (!tutorialScenario) {
      setError(getUILoc('MESSAGES.TUTORIAL_SCENARIO_NOT_FOUND', { id: tutorialScenarioId }));
      return;
    }

    try {
      // 名前ではなくオブジェクトを直接渡す
      const newGameState = initializeGame(gameData.cardDefs, null, playerDeck, npcDeck, tutorialScenario);

      if (!newGameState) {
        throw new Error(getUILoc('MESSAGES.INIT_FAILURE'));
      }

      const finalGameState = produce(newGameState, draftState => {
        draftState.tutorial = {
            scenarioId: tutorialScenarioId,
            currentStep: 0,
            master: tutorialScenario // シナリオ全体を渡す
        };
      });

      gameOverAnimationInitiatedRef.current = false;
      setGameState(finalGameState);
      setCurrentScreen('game');
      setError(null);
      console.log('[App] Setting gameState and currentScreen to "game"');
    } catch (error) {
      console.error('[App] Error initializing tutorial game:', error);
      setError(getUILoc('MESSAGES.TUTORIAL_INIT_FAILURE', { message: error.message }));
      setCurrentScreen('title');
    }
  }, [gameData]);

  const handleGameResultAnimationComplete = useCallback(() => {
    setPlayingGameResultAnimation(false);

    // 世の常モードの場合、対局結果を記録・保存する
    if (gameStateRef.current?.isRoguelikeMode) {
      const npcId = NPC_PLAYER_ID;
      const npcDiscard = gameStateRef.current.players[npcId].discard || [];
      const npcDeck = gameStateRef.current.players[npcId].deck || [];
      const npcHand = gameStateRef.current.players[npcId].hand || [];
      const npcField = gameStateRef.current.players[npcId].field || [];
      const enemyPool = [...npcDiscard, ...npcDeck, ...npcHand, ...npcField].map(c => c.id);
      
      const isVictory = gameStateRef.current.winner === HUMAN_PLAYER_ID;
      yonotsune.recordMatchResult(isVictory, enemyPool);
      
      // 「対話の秋」対局で敗北した場合は、歴史の途絶（Game Over画面）を出さずに直接ノード選択画面へ戻る
      if (!isVictory && yonotsune.activeRun && yonotsune.activeRun.isOpportunityBattle) {
        yonotsune.clearOpportunityBattle();
        yonotsune.goToNextNodeSelection();
        setCurrentScreen('yonotsune');
        return;
      }

      // 最後の戦闘(ボス戦)の場合、報酬画面が出ない可能性があるのでここで勝利報酬を適用する
      // activeRunの情報を基にフェーズ6勝利かどうか判定する
      if (isVictory && yonotsune.activeRun && yonotsune.activeRun.currentPhase === 6) {
          yonotsune.applyBattleRewards(null, null, null, true);
      }
    }

    // 通常対戦（NPC戦）の場合、プレイ記録を保存
    const currentGameState = gameStateRef.current;
    if (currentGameState && !currentGameState.isRoguelikeMode && !currentGameState.isHypothesisMode && !currentGameState.tutorial) {
      recordMatchFromGameState(currentGameState, appVersion);
    }

    // すべてのモードで一度GameOver画面へ遷移し、その中でRoguelikeなら報酬モーダルを表示する
    setCurrentScreen('gameOver');
  }, [yonotsune, appVersion]);

  const handleSystemAnimationComplete = useCallback((event) => {
    const { isGameLocked, lastUpdate } = event.detail;
    setGameState(currentGameState => produce(currentGameState, draft => {
      if (draft) {
        draft.isAnimationLocked = isGameLocked;
        draft.lastUpdate = lastUpdate;
      }
    }));
  }, []);

  useEffect(() => {
    window.addEventListener('gameResultAnimationComplete', handleGameResultAnimationComplete);
    window.addEventListener('systemAnimationComplete', handleSystemAnimationComplete);
    
    return () => {
      window.removeEventListener('gameResultAnimationComplete', handleGameResultAnimationComplete);
      window.removeEventListener('systemAnimationComplete', handleSystemAnimationComplete);
    };
  }, [handleGameResultAnimationComplete, handleSystemAnimationComplete]);

  const handleConfirmMulligan = useCallback((playerId, selectedCardIds) => {
      if (!gameStateRef.current || gameStateRef.current.mulligan_state[playerId].status === 'decided') return;
  
      const newGameState = performMulligan(gameStateRef.current, playerId, selectedCardIds);
      setGameState(newGameState);
  }, []);

  useEffect(() => {
    if (gameState?.phase !== GamePhase.MULLIGAN) return;

    const { mulligan_state } = gameState;
    const humanDecided = mulligan_state[HUMAN_PLAYER_ID]?.status === 'decided';
    const npcDecided = mulligan_state[NPC_PLAYER_ID]?.status === 'decided';

    if (humanDecided && npcDecided) {
        const timer = setTimeout(() => {
            setGameState(gs => {
                if (gs.phase === GamePhase.MULLIGAN) {
                    return resolveMulliganPhase(gs);
                }
                return gs;
            });
        }, 3000);

        return () => clearTimeout(timer);
    }
  }, [gameState]);

  const handlePlayCard = useCallback((card) => {
    if (!gameStateRef.current || !card || gameStateRef.current.awaiting_input || gameStateRef.current.game_over || gameStateRef.current.isAnimationLocked) {
      return;
    }
    const currentPlayerId = gameStateRef.current.current_turn;
    const newGameState = playCard(gameStateRef.current, currentPlayerId, card.instance_id);
    setGameState(newGameState);
  }, []);

  const handleEndTurn = useCallback(() => {
    if (!gameStateRef.current || gameStateRef.current.awaiting_input || gameStateRef.current.game_over || gameStateRef.current.isAnimationLocked) {
      return;
    }
    let newGameState = endTurn(gameStateRef.current);
    setGameState(newGameState);
  }, []);

  const handleProvideInput = useCallback((chosenCard) => {
    if (!gameStateRef.current || !gameStateRef.current.awaiting_input || gameStateRef.current.game_over || gameStateRef.current.isAnimationLocked) {
      return;
    }
    let newGameState = resolveInput(gameStateRef.current, chosenCard);
    setGameState(newGameState);
  }, []);

  const handleSurrender = useCallback(() => {
    if (!gameStateRef.current || gameStateRef.current.game_over) return;
    if (gameStateRef.current.isHypothesisMode) {
      setGameState(null);
      setCurrentScreen('hypothesisVerification');
      return;
    }
    const newState = produce(gameStateRef.current, draft => {
      draft.surrendered_by = HUMAN_PLAYER_ID;
      draft.players[HUMAN_PLAYER_ID].consciousness = 0;
    });
    setGameState(checkGameOver(newState));
  }, []);

  const handleRedevelopmentPlan = useCallback((playerId, cardInstanceId) => {
    if (!gameStateRef.current || gameStateRef.current.game_over || gameStateRef.current.isAnimationLocked) return;
    const currentState = gameStateRef.current;
    const player = currentState.players[playerId];
    if (!player) return;

    // 使用条件チェック：場の財が場の上限の半数より多い
    const fieldWealthCount = player.field.filter(c => c.card_type === 1).length;
    if (fieldWealthCount <= Math.floor(player.field_limit / 2)) return;

    // 1ターン1度の制限チェック
    if (player.redevelopment_used) return;

    const newState = produce(currentState, draft => {
      draft.effect_queue.push([{
        effect_type: 'PROCESS_REDEVELOPMENT_PLAN',
        args: {
          player_id: playerId,
          card_id: cardInstanceId,
        }
      }, null]);
    });
    setGameState(newState);
  }, []);

  const handleDeckSelectedForGameStart = useCallback((playerDeck) => {
    if (!playerDeck || !playerDeck.name) {
      console.error('Invalid deck selected:', playerDeck);
      return;
    }
    if (!gameData.presetDecks || gameData.presetDecks.length === 0) {
      console.error('No preset decks available');
      return;
    }
    // NPCは常にプリセットデッキからランダム選択
    const availableDecks = gameData.presetDecks.filter(deck => deck.name !== playerDeck.name);
    let npcDeck;
    if (availableDecks.length === 0) {
      console.warn('No different decks available for NPC, picking a random one.');
      npcDeck = gameData.presetDecks[Math.floor(Math.random() * gameData.presetDecks.length)];
    } else {
      npcDeck = availableDecks[Math.floor(Math.random() * availableDecks.length)];
    }
    // デッキオブジェクトを直接渡す
    startGame(playerDeck, npcDeck);
  }, [gameData, startGame]);

  // 世の常モードでの対戦開始ハンドラ
  const handleStartYonotsuneMatch = useCallback(() => {
    if (!yonotsune.activeRun) return;

    const run = yonotsune.activeRun;
    console.log('[DEBUG] Starting Yonotsune Match. Run Scale Bonus:', run.scaleBonus);

    const deckName = run.initialDeckKey 
      ? getUILoc(`YONOTSUNE.INITIAL_DECKS.${run.initialDeckKey}.NAME`)
      : run.selectedDeckName;

    const playerDeck = {
      name: deckName,
      cards: run.playerDeck
    };

    const npcDeck = yonotsune.generateEnemyDeck(
      run.currentPhase,
      run.bossKey,
      run.isDiplomacyBreakdown,
      run.isOpportunityBattle
    );

    const roguelikeParams = {
      shufflePlayerDeck: true,
      shuffleNPCDeck: false,
      enablePlayerMulligan: true,
      enableNPCMulligan: false,
      isRoguelikeMode: true,
      // 加算値（ボーナス）のみを渡す
      playerConsciousnessBonus: run.consciousnessBonus,
      playerScaleBonus: run.scaleBonus,
      npcConsciousnessBonus: npcDeck.consciousness,
      npcScaleBonus: npcDeck.scale
    };

    startGame(playerDeck, npcDeck, null, roguelikeParams);
  }, [yonotsune, startGame]);

  const handleStartHypothesisMatch = useCallback(() => {
    if (!gameData.cardDefs) return;

    const finalPlayerCards = [...hypothesisConfig.playerDeck];
    if (hypothesisConfig.playerAntiDeckOut) {
      for (let i = 0; i < 10; i++) finalPlayerCards.push(7);
    }

    const finalNpcCards = [...hypothesisConfig.npcDeck];
    if (hypothesisConfig.npcAntiDeckOut) {
      for (let i = 0; i < 10; i++) finalNpcCards.push(7);
    }

    const playerDeckObj = {
      name: getUILoc('LABELS.PLAYER_SETTINGS'),
      cards: finalPlayerCards
    };

    const npcDeckObj = {
      name: getUILoc('LABELS.OPPONENT_SETTINGS'),
      cards: finalNpcCards
    };

    const hypothesisParams = {
      shufflePlayerDeck: false,
      shuffleNPCDeck: false,
      playerInitialHandSize: hypothesisConfig.playerInitialHandSize,
      npcInitialHandSize: hypothesisConfig.npcInitialHandSize,
      playerConsciousness: hypothesisConfig.playerConsciousness,
      playerScale: hypothesisConfig.playerScale,
      npcConsciousness: hypothesisConfig.npcConsciousness,
      npcScale: hypothesisConfig.npcScale,
      firstPlayer: hypothesisConfig.turnOrder === 'player' ? 'HUMAN' : (hypothesisConfig.turnOrder === 'npc' ? 'NPC' : null),
      enableMulligan: false,
    };

    try {
      const newGameState = initializeGame(
        gameData.cardDefs,
        null,
        playerDeckObj,
        npcDeckObj,
        null,
        null,
        hypothesisParams
      );

      if (!newGameState) {
        throw new Error(getUILoc('MESSAGES.INIT_FAILURE'));
      }

      gameOverAnimationInitiatedRef.current = false;
      setGameState(newGameState);
      setCurrentScreen('game');
      setError(null);
    } catch (err) {
      console.error('[App] Error starting hypothesis match:', err);
      setError(`${getUILoc('MESSAGES.INIT_FAILURE')}: ${err.message}`);
    }
  }, [gameData, hypothesisConfig]);

  const handleBackToTitleFromHypothesis = useCallback(() => {
    setHypothesisConfig(DEFAULT_HYPOTHESIS_CONFIG);
    setCurrentScreen('title');
  }, []);

  const handleGameEnd = () => {
    if (gameStateRef.current?.isHypothesisMode) {
      setCurrentScreen('hypothesisVerification');
      setGameState(null);
      return;
    }
    setCurrentScreen('title');
    setGameState(null);
  };

  const handleScreenChange = (screen, data = {}) => {
    setDeckToEdit(data.deckToEdit || null);
    setCurrentScreen(screen);
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'title':
        return <TitleScreen
                  onScreenChange={handleScreenChange}
                  version={appVersion}
                  onShowRules={() => setShowRules(true)}
                  onShowCredits={() => setShowCredits(true)}
                  onShowChangelog={() => setShowChangelog(true)}
                  language={language}
                  onLanguageChange={handleLanguageChange}
                />;
      case 'deckSelection':
        return (
          <DeckSelectionScreen
            presetDecks={gameData.presetDecks}
            cardDefs={gameData.cardDefs}
            onDeckSelected={handleDeckSelectedForGameStart}
            onBack={() => handleScreenChange('title')}
            onScreenChange={handleScreenChange}
          />
        );
      case 'cardLibrary':
        return <CardLibraryScreen cardDefs={gameData.cardDefs} onBack={() => handleScreenChange('title')} />;
      case 'tutorialSelection':
        return <TutorialSelectionScreen gameData={gameData} onStartTutorial={handleStartTutorial} onExit={() => handleScreenChange('deckSelection')} />;
      case 'deckBuilder':
        return <DeckBuilderScreen gameData={gameData} onExit={() => handleScreenChange('title')} deckToEdit={deckToEdit} />;
      case 'yonotsune':
        return (
          <YonotsuneScreen
            yonotsune={yonotsune}
            cardDefs={gameData.cardDefs}
            onStartMatch={handleStartYonotsuneMatch}
            onBackToTitle={() => handleScreenChange('title')}
          />
        );
      case 'yonotsuneReward':
        return (
          <YonotsuneRewardScreen
            yonotsune={yonotsune}
            gameState={gameState}
            onReturnToMenu={() => handleScreenChange('yonotsune')}
            onScreenChange={handleScreenChange}
          />
        );
      case 'hypothesisVerification':
        return (
          <HypothesisVerificationScreen
            gameData={gameData}
            config={hypothesisConfig}
            onUpdateConfig={setHypothesisConfig}
            onStartExperiment={handleStartHypothesisMatch}
            onBackToTitle={handleBackToTitleFromHypothesis}
          />
        );
      case 'game':
        if (!gameState) return null;
        return (
          <Game
            gameState={gameState}
            cardDefs={gameData.cardDefs}
            onPlayCard={handlePlayCard}
            onEndTurn={handleEndTurn}
            onProvideInput={handleProvideInput}
            onSurrender={handleSurrender}
            onGameStateUpdate={setGameState}
            effectMonitor={enhancedLog.getEffectMonitor()}
            enhancedLog={enhancedLog}
            onConfirmMulligan={handleConfirmMulligan}
            onRedevelopmentPlan={handleRedevelopmentPlan}
          />
        );
      case 'gameOver':
        if (!gameState) return null;
        const isTutorial = !!gameState.tutorial;
        
        let yonotsuneRewardModal = null;
        if (gameState.isRoguelikeMode) {
          yonotsuneRewardModal = (
            <div className="yonotsune-reward-modal">
               <YonotsuneRewardScreen
                  yonotsune={yonotsune}
                  gameState={gameState}
                  onReturnToMenu={() => handleScreenChange('yonotsune')}
                  onScreenChange={handleScreenChange}
               />
            </div>
          );
        }

        return (
          <GameOverScreen
            winnerName={gameState.players[gameState.winner]?.name || 'Unknown'}
            turnHistory={gameState.turnHistory}
            gameState={gameState}
            isTutorial={isTutorial}
            onNewGame={() => handleScreenChange(isTutorial ? 'tutorialSelection' : 'deckSelection')}
            onMainMenu={handleGameEnd}
            yonotsuneRewardModal={yonotsuneRewardModal}
          />
        );
      default:
        return <TitleScreen 
                  onScreenChange={handleScreenChange} 
                  version={appVersion} 
                  language={language} 
                  onLanguageChange={handleLanguageChange} 
                />;
    }
  };

  return (
    <div className="App" key={language}>
      <div className="scale-wrapper" style={scaleStyle}>
        {loading ? (
          <div className="loading">
            <h1>{getUILoc('MESSAGES.LOADING_GAME_DATA')}</h1>
            <p>{getUILoc('MESSAGES.LOADING_DETAILS')}</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <h1>{getUILoc('MESSAGES.ERROR_OCCURRED')}</h1>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>{getUILoc('MESSAGES.RELOAD')}</button>
          </div>
        ) : (
          <>
            {renderScreen()}
            {showRules && <RulesOverlay isOpen={showRules} onClose={() => setShowRules(false)} />}
            {showCredits && <CreditsOverlay isOpen={showCredits} onClose={() => setShowCredits(false)} />}
            {showChangelog && <ChangelogModal isOpen={showChangelog} onClose={() => setShowChangelog(false)} version={appVersion} />}
            {showTranslationWarning && (
              <div
                className="overlay-modal"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  zIndex: 13100
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTranslationWarning(false);
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    maxWidth: '600px',
                    padding: '30px',
                    backgroundColor: '#1a1a2e',
                    border: '2px solid #ff6b6b',
                    borderRadius: '10px',
                    color: '#fff',
                    width: 'auto'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 style={{ marginBottom: '20px', color: '#ff6b6b' }}>{getUILoc('MESSAGES.AUTO_TRANSLATION_WARNING_TITLE')}</h2>
                  <p style={{ marginBottom: '15px', lineHeight: '1.6', color: '#ccc' }}>{getUILoc('MESSAGES.AUTO_TRANSLATION_WARNING_LINE1')}</p>
                  <p style={{ marginBottom: '15px', lineHeight: '1.6', color: '#ccc' }}>{getUILoc('MESSAGES.AUTO_TRANSLATION_WARNING_LINE2')}</p>
                  <p style={{ marginBottom: '25px', lineHeight: '1.6', color: '#ccc' }}>{getUILoc('MESSAGES.AUTO_TRANSLATION_WARNING_LINE3')}</p>
                  <button
                    onClick={() => setShowTranslationWarning(false)}
                    style={{
                      padding: '10px 30px',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      background: '#ff6b6b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '5px',
                      fontWeight: 'bold'
                    }}
                  >
                    {getUILoc('BUTTONS.CLOSE')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
